import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import BackpackPeopleSection from '../components/BackpackPeopleSection';
import { toast } from 'sonner';
import {
  Settings, Mail, Camera, Share2, MapPin,
  ChevronRight, Clock, Users, Copy, Link2, UserCircle, Landmark,
  History as HistoryIcon, Heart as HeartIcon, ImagePlus, Flag,
  Backpack as BackpackIcon, Tent as TentIcon,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * BACKPACK — Tappable-row sections, always-visible scaffold.
 *
 * Order:
 *   1. Profile Header (banner + avatar, both uploadable)
 *   2. Invite / Share
 *   3. Your Activity  (row → /backpack/activity)
 *   4. People you've done things with (row → /backpack/people)
 *   5. Memory (row → /backpack/memory)
 *   6. Groups (row → /groups)
 *   7. Circles (row → /circles)
 *   8. Commons (row → /commons)
 *   9. SCUTA (row → /scuta/explore)
 *
 * Every row shows icon + title + subtitle + chevron.
 * Subtitle = count when data exists, quiet hint when empty.
 */
export default function Backpack() {
  const navigate = useNavigate();
  const { user, authResolved } = useContext(AuthContext);

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const [myActivities, setMyActivities] = useState([]);
  const [connections, setConnections] = useState([]);
  const [savedMemories, setSavedMemories] = useState([]);
  const [pendingMemoryCount, setPendingMemoryCount] = useState(0);
  const [memoryPhotos, setMemoryPhotos] = useState([]);
  const [hostSummaries, setHostSummaries] = useState([]);
  const [groups, setGroups] = useState([]);
  const [circles, setCircles] = useState([]);
  const [commons, setCommons] = useState([]);
  const [circleStream, setCircleStream] = useState([]);

  const [invites, setInvites] = useState(null);
  const [unreadInbox, setUnreadInbox] = useState(0);

  const [showInviteFlow, setShowInviteFlow] = useState(false);
  const [generatingCode, setGeneratingCode] = useState(false);

  const avatarInputRef = useRef(null);
  const bannerInputRef = useRef(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);

  // Sponsor inbox indicator — quiet presence signal, identical to Base Camp.
  // Renders a small sage dot on the avatar only when the current user owns a
  // sponsor account AND has at least one pending support ask.
  const [hasPendingAsk, setHasPendingAsk] = useState(false);
  useEffect(() => {
    if (!user) { setHasPendingAsk(false); return; }
    let mounted = true;
    axios.get(`${API}/api/support/requests/presence`, { withCredentials: true })
      .then(res => {
        if (!mounted) return;
        const isSponsor = Boolean(res.data?.is_sponsor);
        const pending = Boolean(res.data?.has_pending);
        setHasPendingAsk(isSponsor && pending);
      })
      .catch(() => { if (mounted) setHasPendingAsk(false); });
    return () => { mounted = false; };
  }, [user]);

  useEffect(() => {
    if (!authResolved) return;                // wait until /me finishes
    if (!user) { navigate('/auth?mode=signin'); return; }
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authResolved]);

  const loadAll = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [
        profileRes, inboxRes, actRes, inviteRes, connRes,
        groupsRes, circlesRes, commonsRes, savedMemRes, pendingMemRes, streamRes,
        photosRes, hostRes,
      ] = await Promise.all([
        axios.get(`${API}/api/user/profile`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API}/api/messages/unread-count`, { headers }).catch(() => ({ data: { count: 0 } })),
        axios.get(`${API}/api/activities/my-activities`, { headers }).catch(() => ({ data: { activities: [] } })),
        axios.get(`${API}/api/invites/my-invites`, { headers }).catch(() => ({ data: { invites_remaining: 3, codes: [] } })),
        axios.get(`${API}/api/invites/connections`, { headers }).catch(() => ({ data: { connections: [] } })),
        axios.get(`${API}/api/groups/my-groups`, { headers }).catch(() => ({ data: { groups: [] } })),
        axios.get(`${API}/api/circles`, { headers }).catch(() => ({ data: { circles: [] } })),
        axios.get(`${API}/api/commons/my-commons`, { headers }).catch(() => ({ data: { commons: [] } })),
        axios.get(`${API}/api/after/memories?limit=200`, { headers }).catch(() => ({ data: { memories: [] } })),
        axios.get(`${API}/api/after/recent?limit=50`, { headers }).catch(() => ({ data: { memories: [] } })),
        axios.get(`${API}/api/circles/stream?limit=60`, { headers }).catch(() => ({ data: { activities: [] } })),
        axios.get(`${API}/api/after/my-photos?limit=6`, { headers }).catch(() => ({ data: { photos: [] } })),
        axios.get(`${API}/api/after/host-summary?limit=5`, { headers }).catch(() => ({ data: { hosts: [] } })),
      ]);
      setProfile(profileRes.data || {});
      setUnreadInbox(inboxRes.data?.count || 0);
      setMyActivities(actRes.data?.activities || []);
      setInvites(inviteRes.data);
      setConnections(connRes.data?.connections || []);
      setGroups(groupsRes.data?.groups || []);
      setCircles(circlesRes.data?.circles || []);
      setCommons(commonsRes.data?.commons || []);
      setSavedMemories(savedMemRes.data?.memories || []);
      setPendingMemoryCount(((pendingMemRes.data?.memories) || []).filter(m => !m.has_memory).length);
      setCircleStream(streamRes.data?.activities || []);
      setMemoryPhotos(photosRes.data?.photos || []);
      setHostSummaries(hostRes.data?.hosts || []);
    } finally { setLoading(false); }
  };

  // ---- Image upload ----
  // Client-side resize: max 1600px on the long edge, JPEG quality 0.82.
  // A typical phone photo (~3MB JPEG) lands around 250–450KB after this,
  // well under any reasonable ingress/proxy body-size limit in production.
  // Animated GIFs/SVGs would be flattened, but we only accept image/* here.
  const resizeImageToDataUrl = (file, maxEdge = 1600, quality = 0.82) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = reject;
      reader.onload = () => {
        const img = new Image();
        img.onerror = reject;
        img.onload = () => {
          const w = img.naturalWidth;
          const h = img.naturalHeight;
          const scale = Math.min(1, maxEdge / Math.max(w, h));
          const targetW = Math.max(1, Math.round(w * scale));
          const targetH = Math.max(1, Math.round(h * scale));
          const canvas = document.createElement('canvas');
          canvas.width = targetW;
          canvas.height = targetH;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, targetW, targetH);
          // Always re-encode as JPEG — predictable size, no alpha for banners.
          resolve(canvas.toDataURL('image/jpeg', quality));
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    });

  const uploadImageField = async (file, fieldName, setBusy) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image'); return; }
    if (file.size > 12 * 1024 * 1024) { toast.error('Image must be under 12MB'); return; }
    setBusy(true);
    try {
      const dataUrl = await resizeImageToDataUrl(file, 1600, 0.82);
      const token = localStorage.getItem('token');
      const payload = {};
      payload[fieldName] = dataUrl;
      if (fieldName === 'picture') payload.profile_image = dataUrl;
      await axios.put(
        `${API}/api/profile/update`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(fieldName === 'picture' ? 'Profile photo updated' : 'Banner updated');
      setProfile(prev => ({ ...(prev || {}), ...payload }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  const shareProfile = () => {
    const url = `${window.location.origin}/u/${profile?.username || profile?.user_id}`;
    if (navigator.share) {
      navigator.share({ url, title: profile?.name || 'LikeKind' }).catch(() => {});
    } else {
      navigator.clipboard.writeText(url);
      toast.success('Profile link copied');
    }
  };

  // ---- Derivations ----
  // Fallback chain: profile fetch → AuthContext user → safe defaults.
  // This guarantees the header renders even if /api/user/profile is slow
  // or silently fails (cookie-only auth, stale token, etc.).
  const effective = { ...(user || {}), ...(profile || {}) };
  const handle = effective.username ? `@${effective.username}` : '@you';
  const displayName = effective.name || 'You';
  const location = effective.base_state || '';
  const avatarUrl = (effective.picture || effective.profile_image) || `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}&background=6B8F71&color=fff&size=160`;
  const bannerUrl = (effective.header_background && effective.header_background.trim()) || (effective.cover_photo && effective.cover_photo.trim()) || null;

  const now = new Date();
  const hosted = myActivities.filter(a => a.my_role === 'host');
  const participating = myActivities.filter(a => a.my_role !== 'host');
  const going = participating.filter(a => (a.my_signal === 'in') && (!a.start_time || new Date(a.start_time) >= now));
  const past = participating.filter(a => a.start_time && new Date(a.start_time) < now);
  const activityTotal = going.length + past.length + hosted.length;
  const memoryTotal = pendingMemoryCount + savedMemories.length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: 'var(--lk-sage)' }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }} data-testid="backpack-page">

      {/* TOP BAR */}
      <header className="sticky top-0 z-50" style={{ backgroundColor: 'var(--fy-surface-card)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-2xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <h1 className="text-lg font-bold flex items-center gap-2" style={{ color: 'var(--fy-text-primary)' }}>
              <BackpackIcon className="w-5 h-5" style={{ color: 'var(--lk-sage)' }} />
              Backpack
            </h1>
            <div className="flex items-center gap-1">
              <button
                onClick={() => navigate('/base-camp')}
                className="p-2 rounded-full hover:opacity-70"
                style={{ color: 'var(--fy-text-secondary)' }}
                data-testid="bp-basecamp-btn"
                aria-label="Go to Base Camp"
                title="Base Camp"
              >
                <TentIcon className="w-5 h-5" />
              </button>
              <button onClick={() => navigate('/settings')} className="p-2 rounded-full hover:opacity-70" style={{ color: 'var(--fy-text-secondary)' }} data-testid="bp-settings-btn">
                <Settings className="w-5 h-5" />
              </button>
              <button onClick={() => navigate('/inbox')} className="p-2 rounded-full hover:opacity-70 relative" style={{ color: 'var(--fy-text-secondary)' }} data-testid="bp-inbox-header-btn" aria-label="Messages">
                <Mail className="w-5 h-5" />
                {unreadInbox > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 text-[10px] font-bold rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--lk-sage)', color: '#fff' }} data-testid="bp-inbox-unread-badge">
                    {unreadInbox > 9 ? '9+' : unreadInbox}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto pb-24">

        {/* 1. PROFILE HEADER — banner + avatar (both uploadable) */}
        <div className="relative w-full overflow-hidden group" style={{ height: '220px' }} data-testid="profile-banner">
          {bannerUrl ? (
            <img src={bannerUrl} alt="" className="w-full h-full object-cover" />
          ) : (
            /* Default banner — atmospheric blend (mist → sky → sand).
               Per Environmental Color Directive: Backpack banner may use
               subtle Mist/Sky/Lake atmospheric tones for emotional openness. */
            <div
              className="w-full h-full"
              style={{
                background: 'linear-gradient(180deg, var(--atmos-mist) 0%, var(--atmos-sky) 60%, var(--ground-sand) 100%)',
              }}
            />
          )}
          {/* Top-down overlay softened — only a whisper of shade for legibility behind the upload button. */}
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.12) 0%, transparent 50%)' }} />
          {/* Banner upload button */}
          <button
            onClick={() => bannerInputRef.current?.click()}
            className="absolute top-3 right-3 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium backdrop-blur-sm"
            style={{ backgroundColor: 'rgba(0,0,0,0.45)', color: '#fff' }}
            data-testid="edit-banner-btn"
            disabled={uploadingBanner}
          >
            <ImagePlus className="w-3.5 h-3.5" />
            {uploadingBanner ? 'Uploading...' : (bannerUrl ? 'Change banner' : 'Add banner')}
          </button>
          <input type="file" ref={bannerInputRef} className="hidden" accept="image/*" onChange={(e) => uploadImageField(e.target.files?.[0], 'header_background', setUploadingBanner)} />
        </div>

        {/* Profile identity card — sits cleanly below the banner with the avatar tucked into the top-left corner. */}
        <div className="px-4 -mt-10 relative z-10" data-testid="profile-overlay">
          <div
            className="rounded-2xl p-4 flex items-start gap-4"
            style={{
              backgroundColor: 'var(--fy-surface-card)',
              border: '1px solid var(--fy-border-subtle)',
              boxShadow: '0 4px 18px rgba(0,0,0,0.05)',
            }}
          >
            <div className="relative flex-shrink-0 -mt-12">
              <button onClick={() => avatarInputRef.current?.click()} disabled={uploadingAvatar} aria-label="Change profile photo">
                <img
                  src={avatarUrl}
                  alt=""
                  className="w-24 h-24 rounded-full object-cover"
                  style={{ border: '4px solid var(--fy-surface-card)', boxShadow: '0 6px 18px rgba(0,0,0,0.12)' }}
                  onError={(e) => { e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}&background=6B8F71&color=fff&size=160`; }}
                  data-testid="profile-avatar"
                />
                <div className="absolute -bottom-1 -right-1 p-1.5 rounded-full shadow-md" style={{ backgroundColor: 'var(--lk-sage)' }}>
                  <Camera className="w-3 h-3 text-white" />
                </div>
              </button>
              {hasPendingAsk && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); navigate('/support/requests'); }}
                  aria-label="Support asks waiting"
                  data-testid="sponsor-inbox-indicator"
                  className="absolute top-1 right-1 w-2.5 h-2.5 rounded-full focus:outline-none"
                  style={{
                    backgroundColor: 'var(--lk-sage)',
                    opacity: 0.75,
                    border: '1.5px solid var(--fy-surface-card)',
                    padding: 0,
                  }}
                />
              )}
              <input type="file" ref={avatarInputRef} className="hidden" accept="image/*" onChange={(e) => uploadImageField(e.target.files?.[0], 'picture', setUploadingAvatar)} />
            </div>
            <div className="min-w-0 flex-1 pt-1">
              <h2 className="text-2xl font-extrabold leading-tight truncate" style={{ color: 'var(--fy-text-primary)', fontFamily: 'Outfit, system-ui' }} data-testid="profile-name">
                {displayName}
              </h2>
              <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>{handle}</p>
              {location && (
                <p className="text-xs flex items-center gap-1 mt-1" style={{ color: 'var(--fy-text-disabled)' }}>
                  <MapPin className="w-3 h-3" />{location}
                </p>
              )}
              <button onClick={() => navigate('/settings/my-world')} className="text-xs mt-2 font-medium hover:underline" style={{ color: 'var(--lk-sage)' }} data-testid="edit-profile-link">
                Edit profile
              </button>
            </div>
          </div>
        </div>

        {/* 2. INVITE / SHARE */}
        <div className="px-4 mt-4 flex gap-2" data-testid="action-row">
          <Button onClick={() => setShowInviteFlow(!showInviteFlow)} className="flex-1 h-9 rounded-full text-sm font-medium" style={{ backgroundColor: 'var(--lk-sage)', color: '#fff' }} data-testid="invite-someone-btn">
            <Link2 className="w-4 h-4 mr-1.5" />
            Invite Someone {invites ? `(${invites.invites_remaining})` : ''}
          </Button>
          <Button onClick={shareProfile} variant="outline" className="flex-1 h-9 rounded-full text-sm font-medium" style={{ borderColor: 'var(--fy-border-subtle)', color: 'var(--fy-text-primary)' }} data-testid="share-profile-btn">
            <Share2 className="w-4 h-4 mr-1.5" />
            Share Profile
          </Button>
        </div>

        {showInviteFlow && invites && (
          <Card className="mx-4 mt-4 p-4 space-y-3" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }} data-testid="invite-flow">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold" style={{ color: 'var(--fy-text-primary)' }}>Invite Someone</h3>
              <button onClick={() => setShowInviteFlow(false)} className="text-xs" style={{ color: 'var(--fy-text-disabled)' }}>Close</button>
            </div>
            <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>
              {invites.invites_remaining} of {invites.max_invites || 3} invites remaining
            </p>
            {(invites.codes || []).filter(c => c.is_active && !c.redeemed_by).map(code => (
              <div key={code.invite_id} className="flex items-center gap-2 p-2 rounded-lg" style={{ backgroundColor: 'var(--fy-surface-muted)' }}>
                <span className="text-sm font-mono flex-1" style={{ color: 'var(--fy-text-primary)' }}>{code.code}</span>
                <button
                  onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/auth?mode=signup&invite=${code.code}`); toast.success('Invite link copied'); }}
                  className="p-1.5 rounded-md hover:opacity-70"
                  style={{ backgroundColor: 'var(--fy-surface-card)' }}
                  data-testid={`copy-invite-${code.invite_id}`}
                >
                  <Copy className="w-3.5 h-3.5" style={{ color: 'var(--fy-text-secondary)' }} />
                </button>
              </div>
            ))}
            {(invites.invites_remaining || 0) > 0 && (
              <Button
                onClick={async () => {
                  setGeneratingCode(true);
                  try {
                    const token = localStorage.getItem('token');
                    const res = await axios.post(`${API}/api/invites/generate`, {}, { headers: { Authorization: `Bearer ${token}` } });
                    toast.success(`Invite created: ${res.data.code}`);
                    loadAll();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Failed to generate invite');
                  } finally { setGeneratingCode(false); }
                }}
                disabled={generatingCode}
                variant="outline"
                className="w-full h-9 rounded-full text-sm"
                data-testid="generate-invite-btn"
              >
                {generatingCode ? 'Generating...' : 'Generate new invite'}
              </Button>
            )}
            {(invites.invites_remaining || 0) === 0 && (
              <p className="text-xs text-center" style={{ color: 'var(--fy-text-disabled)' }}>
                Invites return when the people you invite participate in an activity.
              </p>
            )}
          </Card>
        )}

        {/* 2.5 PEOPLE — auto-derived contacts (Wave 2). Renders nothing when empty. */}
        <BackpackPeopleSection token={localStorage.getItem('token')} />

        {/* 3. YOUR ACTIVITY — always visible, horizontal scroll */}
        <section className="mt-10" data-testid="your-activity-section">
          <SectionHeader
            eyebrow="YOUR ACTIVITY"
            title="What you're doing"
            tint="sage"
            actionLabel={activityTotal > 0 ? 'See all' : null}
            onAction={() => navigate('/backpack/activity')}
            testid="header-your-activity"
          />
          {activityTotal > 0 ? (
            <HorizontalActivityStream
              acts={[...going, ...hosted, ...past].slice(0, 8)}
              navigate={navigate}
              testidPrefix="bp-ya"
            />
          ) : (
            <EmptySectionTile
              testid="your-activity-empty"
              tint="sage"
              icon={<Flag className="w-5 h-5" style={{ color: 'var(--lk-sage)' }} />}
              primary="Nothing on your calendar yet."
              ctaLabel="Browse nearby activities"
              onCta={() => navigate('/base-camp')}
            />
          )}
        </section>

        {/* 4. FROM YOUR CIRCLES — always visible, horizontal scroll */}
        <section className="mt-8" data-testid="from-your-circles">
          <SectionHeader
            eyebrow="FROM YOUR CIRCLES"
            title="What the people you know are doing"
            tint="amber"
            testid="header-from-your-circles"
          />
          {circleStream.length > 0 ? (
            <HorizontalActivityStream
              acts={circleStream}
              navigate={navigate}
              testidPrefix="bp-cs"
              showSharedBy
            />
          ) : (
            <EmptySectionTile
              testid="from-your-circles-empty"
              tint="amber"
              icon={<UserCircle className="w-5 h-5" style={{ color: '#B5825A' }} />}
              primary="When your circles share activities, they'll live here."
              secondary="Create a circle and invite the people you actually do things with."
              ctaLabel="Create a circle"
              onCta={() => navigate('/circles')}
            />
          )}
        </section>

        {/* PHOTOS — recent approved photos from past activities */}
        {memoryPhotos.length > 0 && (
          <section className="mt-8" data-testid="backpack-photos-section">
            <SectionHeader
              eyebrow="PHOTOS"
              title="Moments from your activities"
              tint="plum"
              actionLabel="See all"
              onAction={() => navigate('/backpack/memory')}
              testid="header-photos"
            />
            <div className="px-4 flex gap-2 overflow-x-auto no-scrollbar snap-x snap-mandatory pb-1" data-testid="backpack-photos-grid">
              {memoryPhotos.slice(0, 8).map(p => (
                <button
                  key={p.photo_id}
                  type="button"
                  onClick={() => navigate(`/activities/${p.activity_id}`)}
                  className="relative flex-shrink-0 w-28 h-28 rounded-xl overflow-hidden snap-start active:scale-[0.98] transition-transform"
                  style={{ backgroundColor: 'var(--fy-surface-muted)' }}
                  data-testid={`backpack-photo-${p.photo_id}`}
                >
                  <img src={p.url} alt="" loading="lazy" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </section>
        )}

        {/* AS HOST — quiet summary of activities you recently hosted. */}
        {hostSummaries.length > 0 && (
          <section className="mt-8" data-testid="as-host-section">
            <SectionHeader
              eyebrow="AS HOST"
              title="Activities you ran"
              tint="forest"
              testid="header-as-host"
            />
            <div className="px-4 space-y-2" data-testid="as-host-list">
              {hostSummaries.map(h => {
                const bits = [];
                if (h.participant_count > 0) {
                  bits.push(`${h.participant_count} ${h.participant_count === 1 ? 'joined' : 'joined'}`);
                }
                if (h.photo_count > 0) bits.push(`${h.photo_count} photo${h.photo_count === 1 ? '' : 's'}`);
                if (h.no_show_count > 0) bits.push(`${h.no_show_count} missed`);
                if (bits.length === 0) bits.push('No activity recorded');
                return (
                  <button
                    key={h.activity_id}
                    type="button"
                    onClick={() => navigate(`/activities/${h.activity_id}`)}
                    className="w-full flex items-center gap-3 p-3 rounded-xl text-left active:scale-[0.99] transition-all hover:shadow-sm"
                    style={{
                      backgroundColor: 'var(--fy-surface-card)',
                      border: '1px solid var(--fy-border-subtle)',
                      borderLeft: '3px solid #4F6D4A',
                    }}
                    data-testid={`as-host-${h.activity_id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate" style={{ color: 'var(--fy-text-primary)' }}>
                        {h.title}
                      </p>
                      <p className="text-xs truncate" style={{ color: 'var(--fy-text-secondary)' }}>
                        {bits.join(' · ')}
                      </p>
                    </div>
                    <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--fy-text-disabled)' }} />
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {/* SECTION ROWS — always visible, live subtitles */}
        {/* SECTION ROWS — always visible, colored accents, live subtitles */}
        <section className="mt-8" data-testid="backpack-collections">
          <SectionHeader
            eyebrow="YOUR COLLECTIONS"
            title="People, memory, places"
            tint="teal"
            testid="header-collections"
          />
          <div className="px-4 space-y-2">

            <SectionRow
              testid="row-people"
              accent="#7A8F5B"
              iconBg="#E6EED9"
              icon={<Users className="w-5 h-5" style={{ color: '#5A7A4A' }} />}
              title="People you've done things with"
              subtitle={connections.length > 0
                ? `${connections.length} ${connections.length === 1 ? 'person' : 'people'}`
                : "People you meet through activities appear here"}
              onClick={() => navigate('/backpack/people')}
              avatars={connections.length > 0
                ? connections.slice(0, 4).map(p => p.picture || p.profile_image || `https://ui-avatars.com/api/?name=${encodeURIComponent(p.name || 'U')}&background=6B8F71&color=fff&size=32`)
                : []}
            />

            <SectionRow
              testid="row-memory"
              accent="#C06C5E"
              iconBg="#F6DCD6"
              icon={<HeartIcon className="w-5 h-5" style={{ color: '#9E4E40' }} />}
              title="Memory"
              subtitle={memoryTotal > 0
                ? `${pendingMemoryCount > 0 ? `${pendingMemoryCount} new` : ''}${pendingMemoryCount > 0 && savedMemories.length > 0 ? ' · ' : ''}${savedMemories.length > 0 ? `${savedMemories.length} saved` : ''}`
                : "Moments you keep from activities appear here"}
              badge={pendingMemoryCount > 0 ? pendingMemoryCount : null}
              onClick={() => navigate('/backpack/memory')}
              photoThumbs={memoryPhotos.slice(0, 3).map(p => p.url)}
            />

            <SectionRow
              testid="row-groups"
              accent="#D89A56"
              iconBg="#FAE4C8"
              icon={<UserCircle className="w-5 h-5" style={{ color: '#AD7436' }} />}
              title="Groups"
              subtitle={groups.length > 0
                ? `${groups.length} ${groups.length === 1 ? 'group' : 'groups'}`
                : "Groups you join appear here · Start one"}
              onClick={() => navigate(groups.length > 0 ? '/backpack/groups' : '/create-group')}
            />

            <SectionRow
              testid="row-circles"
              accent="#6E8FA6"
              iconBg="#DCE8F0"
              icon={<UserCircle className="w-5 h-5" style={{ color: '#4A6E86' }} />}
              title="Circles"
              subtitle={circles.length > 0
                ? `${circles.length} ${circles.length === 1 ? 'circle' : 'circles'}`
                : "Circles you create appear here · Create one"}
              onClick={() => navigate('/circles')}
            />

            <SectionRow
              testid="row-commons"
              accent="#8A7A5C"
              iconBg="#EEE6D4"
              icon={<Landmark className="w-5 h-5" style={{ color: '#6D5F45' }} />}
              title="Commons"
              subtitle={commons.length > 0
                ? `${commons.length} ${commons.length === 1 ? 'commons' : 'commons'}`
                : "Commons you belong to appear here · Explore"}
              onClick={() => navigate(commons.length > 0 ? '/backpack/commons' : '/for/commons')}
            />

            <SectionRow
              testid="row-scuta"
              accent="#4B5056"
              iconBg="#DFE3E6"
              icon={<HistoryIcon className="w-5 h-5" style={{ color: '#3A3F44' }} />}
              title="SCUTA"
              subtitle="Timeline awareness"
              onClick={() => window.open(`${window.location.origin}/scuta/explore`, '_blank', 'noopener,noreferrer')}
            />

          </div>
        </section>

      </main>
    </div>
  );
}


/**
 * SectionHeader — a bolder, warmer header shared across Backpack sections.
 * `tint` is purely decorative: drives the eyebrow colour + accent bar.
 */
const TINTS = {
  sage:   { eyebrow: '#5A7A4A', bar: '#7A8F5B' },
  amber:  { eyebrow: '#A87A3D', bar: '#D89A56' },
  plum:   { eyebrow: '#8E4A58', bar: '#C06C5E' },
  forest: { eyebrow: '#3C5637', bar: '#4F6D4A' },
  teal:   { eyebrow: '#436170', bar: '#6E8FA6' },
};

function SectionHeader({ eyebrow, title, tint = 'sage', actionLabel, onAction, testid }) {
  const t = TINTS[tint] || TINTS.sage;
  return (
    <div className="px-4 mb-3" data-testid={testid}>
      <div className="flex items-end justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="inline-block w-6 h-[3px] rounded-full" style={{ backgroundColor: t.bar }} />
            <p className="text-[10px] font-bold uppercase tracking-[0.12em]" style={{ color: t.eyebrow }}>
              {eyebrow}
            </p>
          </div>
          {title && (
            <h3
              className="text-lg font-extrabold leading-tight truncate"
              style={{ color: 'var(--fy-text-primary)', fontFamily: 'Outfit, system-ui' }}
            >
              {title}
            </h3>
          )}
        </div>
        {actionLabel && (
          <button
            onClick={onAction}
            className="text-xs font-semibold whitespace-nowrap hover:underline"
            style={{ color: t.eyebrow }}
            data-testid={`${testid}-action`}
          >
            {actionLabel} →
          </button>
        )}
      </div>
    </div>
  );
}


/**
 * HorizontalActivityStream — finite horizontal scroll of Activity Cards.
 * Reveals a hint of the next card so users discover it’s scrollable.
 */
function HorizontalActivityStream({ acts, navigate, testidPrefix, showSharedBy }) {
  return (
    <div
      className="flex gap-3 overflow-x-auto no-scrollbar snap-x snap-mandatory pb-2 pl-4 pr-4"
      data-testid={`${testidPrefix}-stream`}
    >
      {acts.map(act => {
        const locationStr = typeof act.location === 'string' ? act.location : (act.location?.name || '');
        const statusLabel = (() => {
          if (act.my_did_not_show) return { text: 'Missed', color: 'var(--fy-text-disabled)' };
          const status = (act.lifecycle_status || '').toLowerCase();
          if (status === 'completed') return { text: 'Completed', color: '#6B7280' };
          if (status === 'active') return { text: 'Happening now', color: '#059669' };
          const count = act.participant_count ?? (act.participants || []).length;
          if (count >= 3) return { text: 'Good to go', color: '#059669' };
          if (count >= 1) return { text: `Needs ${3 - count} more`, color: '#D97706' };
          return { text: 'Needs people', color: '#D97706' };
        })();
        return (
          <div
            key={`${testidPrefix}-${act.activity_id}`}
            className="flex-shrink-0 w-[260px] snap-start rounded-2xl overflow-hidden cursor-pointer active:scale-[0.98] transition-all hover:shadow-md"
            style={{
              backgroundColor: 'var(--fy-surface-card)',
              border: '1px solid var(--fy-border-subtle)',
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            }}
            onClick={() => navigate(`/activities/${act.activity_id}`)}
            data-testid={`${testidPrefix}-${act.activity_id}`}
          >
            <div className="relative w-full h-32 overflow-hidden" style={{ backgroundColor: 'var(--fy-surface-muted)' }}>
              {act.image_url ? (
                <img
                  src={act.image_url}
                  alt=""
                  className="w-full h-full object-cover"
                  loading="lazy"
                  onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
                />
              ) : (
                <div className="w-full h-full" style={{ background: 'linear-gradient(135deg, #E4EADD 0%, #F3ECE0 100%)' }} />
              )}
              <span
                className="absolute top-2 left-2 text-[10px] font-semibold px-2 py-0.5 rounded-full backdrop-blur-sm"
                style={{ backgroundColor: `${statusLabel.color}E0`, color: '#FFFFFF' }}
              >
                {statusLabel.text}
              </span>
            </div>
            <div className="p-3">
              <p className="text-sm font-bold mb-1 truncate" style={{ color: 'var(--fy-text-primary)' }}>{act.title}</p>
              <div className="flex items-center gap-2 text-[11px]" style={{ color: 'var(--fy-text-secondary)' }}>
                {act.start_time && (
                  <span className="flex items-center gap-0.5">
                    <Clock className="w-3 h-3" />
                    {new Date(act.start_time).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                  </span>
                )}
                {locationStr && (
                  <span className="flex items-center gap-0.5 truncate">
                    <MapPin className="w-3 h-3" />
                    {locationStr.length > 18 ? locationStr.slice(0, 18) + '…' : locationStr}
                  </span>
                )}
              </div>
              {showSharedBy && act.shared_by_name && (
                <p className="text-[10px] italic mt-1.5 truncate" style={{ color: 'var(--fy-text-disabled)' }}>
                  via {act.shared_by_name}
                </p>
              )}
            </div>
          </div>
        );
      })}
      {/* trailing spacer so the last card can scroll fully into view */}
      <div className="flex-shrink-0 w-2" aria-hidden="true" />
    </div>
  );
}


/**
 * EmptySectionTile — inviting empty state for horizontal-scroll sections.
 */
function EmptySectionTile({ testid, tint = 'sage', icon, primary, secondary, ctaLabel, onCta }) {
  const t = TINTS[tint] || TINTS.sage;
  return (
    <div className="px-4" data-testid={testid}>
      <div
        className="rounded-2xl p-5 flex items-start gap-3"
        style={{
          background: `linear-gradient(135deg, ${t.bar}15 0%, ${t.bar}08 100%)`,
          border: `1px dashed ${t.bar}55`,
        }}
      >
        <div className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${t.bar}25` }}>
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold" style={{ color: 'var(--fy-text-primary)' }}>{primary}</p>
          {secondary && (
            <p className="text-xs mt-1" style={{ color: 'var(--fy-text-secondary)' }}>{secondary}</p>
          )}
          {ctaLabel && (
            <button
              onClick={onCta}
              className="mt-3 inline-flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-full transition-transform active:scale-95"
              style={{ backgroundColor: t.bar, color: '#FFFFFF' }}
              data-testid={`${testid}-cta`}
            >
              {ctaLabel} →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


/**
 * SectionRow — consistent tappable card for every Backpack collection.
 * Coloured left-accent bar + tinted icon pill give each row its own identity
 * without losing the calm, consistent baseline look.
 */
function SectionRow({ testid, icon, title, subtitle, onClick, badge, avatars, photoThumbs, accent, iconBg }) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 p-3 rounded-xl text-left transition-all active:scale-[0.99] hover:shadow-sm"
      style={{
        backgroundColor: 'var(--fy-surface-card)',
        border: '1px solid var(--fy-border-subtle)',
        borderLeft: accent ? `3px solid ${accent}` : '1px solid var(--fy-border-subtle)',
      }}
      data-testid={testid}
    >
      <div
        className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: iconBg || 'var(--lk-sage-muted, #EDF1E8)' }}
      >
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-semibold truncate" style={{ color: 'var(--fy-text-primary)' }}>{title}</p>
          {badge != null && (
            <span className="text-[10px] font-bold rounded-full px-1.5 min-w-[18px] h-[18px] flex items-center justify-center" style={{ backgroundColor: accent || 'var(--lk-sage)', color: '#fff' }}>
              {badge > 9 ? '9+' : badge}
            </span>
          )}
        </div>
        <p className="text-xs truncate" style={{ color: 'var(--fy-text-secondary)' }}>{subtitle}</p>
      </div>
      {photoThumbs && photoThumbs.length > 0 && (
        <div className="flex -space-x-1.5 flex-shrink-0 mr-1" data-testid={`${testid}-photos`}>
          {photoThumbs.map((src, i) => (
            <img
              key={i}
              src={src}
              alt=""
              loading="lazy"
              className="w-8 h-8 rounded-md object-cover"
              style={{ border: '2px solid var(--fy-surface-card)' }}
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          ))}
        </div>
      )}
      {avatars && avatars.length > 0 && (
        <div className="flex -space-x-2 flex-shrink-0 mr-1">
          {avatars.map((src, i) => (
            <img
              key={i}
              src={src}
              alt=""
              className="w-6 h-6 rounded-full object-cover"
              style={{ border: '2px solid var(--fy-surface-card)' }}
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          ))}
        </div>
      )}
      <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--fy-text-disabled)' }} />
    </button>
  );
}
